import { useState, useContext, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Skeleton } from 'primereact/skeleton';
import { Modal, Button, Spinner } from 'react-bootstrap';
import AuthContext from '../../../auth/AuthContext';
import AlertService from '../../../services/AlertService';
import CategoryCard from "./CategoryCard";
import CatalogueService from "../../../services/CatalogueService";
import { BiSearchAlt } from "react-icons/bi";
import { MdDragIndicator } from "react-icons/md";
import { TbArrowsSort } from "react-icons/tb";

const Catalogues = () => {
  const [fullCategories, setFullCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [reorderList, setReorderList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showEdit, setShowEdit] = useState(false);


  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  // Auth Guard
 useEffect(() => {
    const catalogueAccess = authContext.getPermissions("catalogues") || [];

  if (
    !catalogueAccess.some((f) => f.access.includes(1)) ||
    authContext.onGoingValidations
  ) {
    navigate("/");
  }

  setShowEdit(catalogueAccess.some((f) => f.access.includes(3)));

}, [navigate, authContext]);

  //Load Categories
  const loadCategories = useCallback(
    async (signal) => {
      setLoading(true);
      try {
        const data = await CatalogueService.GetParentCategoryList(
          authContext,
          signal
        );
        if (signal?.aborted) return;
        if (data.isSuccess) {
          setFullCategories(data.data || []);
        } else {
          AlertService.error(data.errorMessage);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [authContext]
  );

  useEffect(() => {
    const controller = new AbortController();
    loadCategories(controller.signal);
    return () => controller.abort();
  }, [loadCategories]);

  // Navigate to Category
  const handleClick = (title, id) => {
    const slug = title.toLowerCase().replace(/\s+/g, "-");
    navigate(`/catalogue-dashboard/catalogue/${slug}`, {
      state: { title, value: id },
    });
  };

  // Search Filter
  const filteredCategories = useMemo(() => {
    if (!searchText) return fullCategories;
    return fullCategories.filter((cat) =>
      cat.label.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [fullCategories, searchText]);

  //Reorder Modal 
  const openReorderModal = () => {
    setReorderList([...fullCategories]);
    setShowReorderModal(true);
  };

  const handleDragStart = (index) => {
    dragItem.current = index;
  };

  const handleDragEnter = (index) => {
    dragOverItem.current = index;
  };

  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    const updated = [...reorderList];
    const dragged = updated.splice(dragItem.current, 1)[0];
    updated.splice(dragOverItem.current, 0, dragged);
    dragItem.current = null;
    dragOverItem.current = null;
    setReorderList(updated);
  };

  const handleSaveOrder = async () => {
    setSaving(true);
    try {
      const payload = reorderList.map((cat, index) => ({
        id: cat.value, 
        orderBy: index + 1,
      }));

      const result = await CatalogueService.SaveCategoryOrder(
        authContext,
        payload
      );

      if (result.isSuccess) {
        setFullCategories(reorderList);
        AlertService.success("Category order saved successfully!");
        setShowReorderModal(false);
          await loadCategories();
      } else {
        AlertService.error(result.errorMessage);
      }
    } catch (err) {
      AlertService.error("Failed to save order. Please try again.");
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="catalogues-wrapper">

      {/*Search + Reorder Button*/}
      <div className="catalogues-search-header">
        <div className="container-fluid py-3">
          <div className="d-flex justify-content-center align-items-center gap-2">

{loading ? (
  <div className="d-flex justify-content-center align-items-center gap-2 w-100" style={{ maxWidth: "700px" }}>
    
    {/* Search Skeleton */}
    <Skeleton height="50px" className="w-100" borderRadius="8px" />

    {/* Button Skeleton */}
    <Skeleton width="50px" height="50px" borderRadius="8px" />

  </div>
) : (
  <>

            {/* Search Input */}
            <div className="position-relative w-100" style={{ maxWidth: "700px" }}>
              <input
                type="text"
                className="form-control custom-input-color py-3 ps-5 pe-5"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <span
                className="position-absolute top-50 translate-middle-y text-muted"
                style={{ left: "16px", zIndex: 10, pointerEvents: "none" }}
              >
                <BiSearchAlt size={22} />
              </span>
              {searchText && (
                <button
                  className="btn position-absolute end-0 top-50 translate-middle-y fw-bold"
                  onClick={() => setSearchText("")}
                  style={{ right: "12px", zIndex: 10 }}
                >
                  ✕
                </button>
              )}
            </div>
    {/* Reorder Icon Button */}
     {showEdit && (
            <button
              className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
              title="Reorder Parent Categories"
              onClick={openReorderModal}
              style={{ height: "50px", width: "50px", flexShrink: 0 }}
            >
              <TbArrowsSort size={20} />
            </button>
     )}
            </>
)}

          </div>
        </div>
      </div>

      {/* ── Category Cards ── */}
      <div className="catalogues-content">
        <div className="container-fluid pt-3">
          <div className="row g-4 mb-4">
            {loading ? (
              Array.from({ length: 8 }).map((_, idx) => (
                <div key={idx} className="col-12 col-sm-6 col-md-4 col-lg-4 col-xl-3">
                  <div className="card border-0 shadow-sm p-3 h-100 d-flex flex-row align-items-center gap-3">
                    <Skeleton shape="circle" size="3rem" />
                    <Skeleton width="80%" height="1.2rem" />
                  </div>
                </div>
              ))
            ) : filteredCategories.length > 0 ? (
              filteredCategories.map((cat) => (
                <div
                  key={cat.value}
                  className="col-12 col-sm-6 col-md-4 col-lg-4 col-xl-3"
                  onClick={() => handleClick(cat.label, cat.value)}
                  style={{ cursor: "pointer" }}
                >
                <CategoryCard title={cat.label} incidentCount={cat.incidentCount} completedIncidentCount={cat.completedCount}/>
                </div>
              ))
            ) : (
              <div className="col-12 text-center">
                No category found...
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      <Modal
        show={showReorderModal}
        onHide={() => !saving && setShowReorderModal(false)}
        centered
        size="lg"  
        backdrop={saving ? "static" : true}
        keyboard={!saving}
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title className="text-center w-100">
            Reorder Parent Categories
          </Modal.Title>
        </Modal.Header>

        <Modal.Body  style={{ maxHeight: "65vh", overflowY: "auto" }}>
          <p className="small mb-3">
            Drag and drop to reorder. Click <strong>Save Order</strong> to apply.
          </p>
          <div>
            {reorderList.map((cat, index) => (
              <div
                key={cat.value}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragEnter={() => handleDragEnter(index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => e.preventDefault()}
                className="d-flex align-items-center gap-2 p-2 ps-3 mb-2 rounded custom-input-color"
                style={{
                  cursor: "grab",
                  userSelect: "none",
                  transition: "box-shadow 0.15s ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.12)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.boxShadow = "none")
                }
              >
                {/* Order Number */}
                <span
                  className=""
                  style={{ minWidth: "24px", fontSize: "13px" }}
                >
                  {index + 1}
                </span>

                {/* Drag Handle */}
                <MdDragIndicator size={20} className="text-muted" />

                {/* Label */}
                <span style={{ fontSize: "14px" }}>{cat.label}</span>
              </div>
            ))}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowReorderModal(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveOrder}
            disabled={saving}
          >
            {saving ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Saving...
              </>
            ) : (
              "Save Order"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default Catalogues;